<script>
  import { onMount } from "svelte";
  import { fade } from "svelte/transition";
  import ExperienceCard from "../components/experience/ExperienceCard.svelte";
  import Timeline from "../components/experience/Timeline.svelte";
  import Header from "../components/Header.svelte";
  import { processExperiences } from "../lib/dateUtils";

  let experiences = [];
  let timelineStart;
  let timelineEnd;
  let isLoading = true;
  let error = null;
  let activeIndex = -1;

  onMount(async () => {
    try {
      isLoading = true;
      let response = await fetch("/experience.json");

      if (!response.ok) {
        throw new Error(
          `Failed to load experiences: ${response.status} ${response.statusText}`,
        );
      }

      let rawExperiences = await response.json();
      console.log("Loaded experiences:", rawExperiences);

      // Process experiences for sorting and overlap detection
      const processedExperiences = processExperiences(rawExperiences);

      // Set timeline dates
      timelineStart = processedExperiences[0].startDate;
      timelineEnd =
        processedExperiences[processedExperiences.length - 1].endDate;

      // Update experiences with the raw data that includes display values
      experiences = rawExperiences;

      // Set first item as active after a delay
      setTimeout(() => {
        activeIndex = 0;
      }, 500);

      console.log("Final experiences:", experiences);
    } catch (err) {
      console.error("Error loading experiences:", err);
      error = err.message || "Failed to load experiences";
    } finally {
      isLoading = false;
    }
  });

  function setActive(index) {
    activeIndex = index;
  }
</script>

<section id="experience" class="relative py-24 overflow-hidden">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <Header title="Experience ( 6+ years )" subtitle="Professional Timeline" />

    {#if isLoading}
      <div
        class="flex flex-col items-center justify-center py-20"
        in:fade={{ duration: 200 }}
      >
        <div
          class="w-12 h-12 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"
        ></div>
        <p class="mt-4 text-gray-500 dark:text-gray-400">
          Loading experience...
        </p>
      </div>
    {:else if error}
      <div
        class="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-700/20 rounded-lg p-4 max-w-xl mx-auto"
        in:fade
      >
        <p class="text-red-600 dark:text-red-400 text-center">{error}</p>
        <button
          class="mt-2 px-4 py-2 bg-red-100 dark:bg-red-800/20 hover:bg-red-200 text-red-600 dark:text-red-400 rounded mx-auto block"
          on:click={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    {:else if experiences.length > 0}
      <!-- Modern stacked layout with interactive timeline -->
      <div
        class="mt-10 lg:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 relative"
      >
        <!-- Left side: Timeline -->
        <div class="lg:col-span-3 relative">
          <Timeline {experiences} {activeIndex} {setActive} />
        </div>

        <!-- Right side: Experience details -->
        <div class="lg:col-span-9 relative flex items-center justify-center">
          <div class="w-full max-w-3xl">
            {#each experiences as exp, i}
              {#if i === activeIndex}
                <ExperienceCard experience={exp} />
              {/if}
            {/each}
          </div>
        </div>
      </div>

      <!-- Mobile experience list (for smaller screens) -->
    {:else}
      <div class="text-center py-16" in:fade>
        <p class="text-gray-500 dark:text-gray-400">
          No experience data available
        </p>
      </div>
    {/if}
  </div>
</section>

<style>
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
  }

  .animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
</style>
